import type {
  WidgetPublicConfig,
  IssueJwtResponse,
  StartSessionResponse,
  NearRealtimeTurnResponse,
  WidgetModality,
} from './types';
import type { HumanChatRequest } from './humanChat';
import type { LinkPreviewData } from './linkPreview';

// Thin wrappers over the 5 anonymous Function endpoints that serve the
// widget runtime. All errors throw with a normalised .message so the UI
// layer can render a single failure path.

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${b}${p}`;
}

async function asJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: any = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    // Prefer the human-readable `message` over the machine slug `error` so
    // the widget surfaces "Virtual Agent is not published." instead of
    // "agent_not_published" to end users.
    const msg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.code = data?.error;
    err.body = data;
    throw err;
  }
  return data as T;
}

export class WidgetApi {
  constructor(private readonly baseUrl: string) {}

  async chatCustomer<T = unknown>(token: string, body: HumanChatRequest): Promise<T> {
    const response = await fetch(joinUrl(this.baseUrl, '/api/chatCustomer'), {
      method: 'POST', credentials: 'omit', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    return asJson<T>(response);
  }

  async getPublicConfig(publicKey: string): Promise<WidgetPublicConfig> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/getWidgetPublicConfig/${encodeURIComponent(publicKey)}`), {
      method: 'GET',
      credentials: 'omit',
    });
    return asJson<WidgetPublicConfig>(res);
  }

  async issueSessionJwt(opts: {
    publicKey: string;
    embedSecret: string;
    modality?: WidgetModality;
    precallFormData?: Record<string, any>;
    sessionFingerprint?: string;
    capturedMetadata?: Record<string, any>;
    consentEvidence?: Record<string, any>;
  }): Promise<IssueJwtResponse> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/issueWidgetSessionJWT`), {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(opts),
    });
    return asJson<IssueJwtResponse>(res);
  }

  async startSession(token: string, opts: {
    modality: WidgetModality;
    widgetSessionId: string;
    /** Caller mic capture mode (from the widget config) — server uses it to gate VAD. */
    pushToTalk?: boolean;
    /** How the agent should reply (text/audio/audio_text/match) — from widget config. */
    responseMode?: 'text' | 'audio' | 'audio_text' | 'match';
    /** Pre-call form answers keyed by contextKey — injected into the agent context. */
    precallFormData?: Record<string, any>;
    capturedMetadata?: Record<string, any>;
    consentEvidence?: Record<string, any>;
  }): Promise<StartSessionResponse> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/startVirtualAgentSession`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson<StartSessionResponse>(res);
  }

  async ingest(token: string, body: {
    callId: string;
    type: 'audio' | 'audio.commit' | 'text' | 'response.cancel' | 'interrupt' | 'playback' | 'richContentSubmit';
    active?: boolean;
    sequence?: number;
    payload?: string;
    text?: string;
    toolCallId?: string;
    formData?: Record<string, any>;
  }): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/ingestVirtualAgentMessage`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return asJson(res);
  }

  async endSession(token: string, callId: string, reason?: string): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/endVirtualAgentSession`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ callId, reason }),
    });
    return asJson(res);
  }

  async submitPostcallForm(token: string, opts: {
    callId: string;
    data: Record<string, any>;
    surveyId?: string;
  }): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/submitWidgetPostcallForm`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  /** Server-side pick of the post-chat survey for this session (conditions + governance stay server-side). */
  async resolvePostcallSurvey(token: string, opts: {
    callId: string;
    context: Record<string, string>;
    /** How the conversation ended: user_ended | agent_ended | remote_closed. */
    reason?: string;
    /** Visitor messages sent during the session. */
    customerTurns?: number;
  }): Promise<{ surveyId: string | null }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/resolveWidgetPostcallSurvey`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  async getLinkPreview(token: string, url: string): Promise<{ ok: boolean; preview: LinkPreviewData | null }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/getWidgetLinkPreview`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ url }),
    });
    return asJson(res);
  }

  /**
   * Per-response rating. Re-sending the same `messageId` replaces the stored
   * entry, so the rating lands immediately and a later comment enriches it.
   */
  async submitFeedback(token: string, opts: {
    callId: string;
    messageId: string;
    rating: 'up' | 'down';
    comment?: string;
    question?: string;
    answer?: string;
  }): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/submitWidgetFeedback`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  /**
   * Runs the IVA tool bound to a quick-reply pill. The backend re-reads the
   * published config and refuses any pill it cannot find there.
   */
  async invokePill(token: string, opts: {
    callId: string;
    pillId: string;
    parameters?: Record<string, any>;
  }): Promise<{ ok: boolean; text?: string; renderDirectives?: any[] }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/invokeWidgetPillTool`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  /**
   * Fires the alert tool attached to a deeplink pill, just before the link
   * opens. The tool id lives on the config — the browser only knows a pill has
   * one via the resolved pill's `notify` flag.
   */
  async notifyPillAlert(token: string, opts: {
    callId: string;
    pillId: string;
    fallbackTranscript?: Array<{ role: string; text: string; timestamp?: string }>;
  }): Promise<{ ok?: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/notifyWidgetPillAlert`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    if (res.status === 204) return {};
    return asJson(res);
  }

  /**
   * Asks the backend to forward the finished conversation's transcript to the
   * visitor's address via the survey field's configured alert tool.
   */
  async requestTranscript(token: string, opts: {
    callId: string;
    email: string;
    fieldId: string;
    source: 'pre' | 'post';
    /** Client-rendered turns, used only when the stored interaction has none yet. */
    fallbackTranscript?: Array<{ role: string; text: string; timestamp?: string }>;
  }): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/requestWidgetTranscript`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  /**
   * Uploads a single file to the Virtual Agent session.
  * Server validates and stores the file, then injects trusted metadata only.
   */
  async uploadFile(token: string, opts: {
    callId: string;
    fileName: string;
    mimeType: string;
    contentBase64: string;
  }): Promise<{ fileId: string; status: 'uploaded' | 'failed'; name: string; mimeType: string; size: number; contentHash?: string }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/uploadWidgetFile`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    return asJson(res);
  }

  /**
   * Send one conversation turn to a NEAR-REAL-TIME Virtual Agent. Unlike the
   * realtime path (streaming over WebPubSub), near-RT is plain request/response:
   * the reply (and optional synthesised audio URL) is returned directly.
   */
  async nearRealtimeTurn(token: string, body: {
    message?: string;
    audioBase64?: string;
    audioMimeType?: string;
    voice?: string;
    fileIds?: string[];
    richContentSubmit?: { toolName: string; formData?: Record<string, any> };
  }): Promise<NearRealtimeTurnResponse> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/nearRealtimeWidgetTurn`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return asJson<NearRealtimeTurnResponse>(res);
  }

  /** End a near-real-time Virtual Agent session. */
  async nearRealtimeEnd(token: string): Promise<{ ok: boolean }> {
    const res = await fetch(joinUrl(this.baseUrl, `/api/nearRealtimeWidgetEnd`), {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });
    return asJson(res);
  }

  /**
   * Fire-and-forget session teardown that survives a page unload (reload / tab
   * close). Uses `keepalive` fetch — unlike `navigator.sendBeacon` it can carry
   * the `Authorization: Bearer` header the end endpoints require, and unlike a
   * normal fetch it is not aborted when the document is torn down. The response
   * is ignored; the runtime end handlers are idempotent.
   */
  endSessionBeacon(token: string, opts: { callId: string; nearRealtime?: boolean; reason?: string }): void {
    try {
      const path = opts.nearRealtime ? `/api/nearRealtimeWidgetEnd` : `/api/endVirtualAgentSession`;
      const body = opts.nearRealtime ? {} : { callId: opts.callId, reason: opts.reason };
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch(joinUrl(this.baseUrl, path), {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: {
          'content-type': 'application/json',
          'authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      }).catch(() => { /* unload — nothing to do */ });
    } catch { /* */ }
  }
}
